import { useMemo, useState } from 'react';
import { Check, Minus, ChevronRight, ChevronDown, X } from 'lucide-react';
import Dropdown from '../receptura/Dropdown';
import { ACCENT as SECTION_COLOR, OK, WARN, BAD } from '../../lib/uiTokens';
import { GRID_POL, seskupPolozky, stavPolozky, leafKategorie, TYP_KATALOG_FK } from './polozkyGrid';

const fmt = (v) => (v == null ? '—'
    : new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v));

const fmtKratce = (v) => (v == null ? '—'
    : new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(v));

const TYP_VOLBY = [
    { value: 'surovina', label: 'Surovina' },
    { value: 'obal', label: 'Obal' },
    { value: 'spotreba', label: 'Spotřební zboží' },
    { value: 'phm', label: 'PHM (palivo)' },
    { value: 'energie', label: 'Energie' },
    { value: 'sluzba', label: 'Služba' },
    { value: 'investice', label: 'Investice' },
    { value: 'ostatni', label: 'Ostatní' },
];

const BARVA_STAVU = { red: BAD, orange: WARN };

/** Zaškrtávátko pro hromadné zařazení — vlastní, ať ladí se seznamem dokladů. */
function Vyberka({ checked, indeterminate = false, onClick, size = 18 }) {
    const aktivni = checked || indeterminate;
    return (
        <button type="button" role="checkbox" aria-checked={checked}
            onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
            className="flex-shrink-0 rounded flex items-center justify-center transition-colors"
            style={{
                width: size, height: size, color: '#fff',
                border: `1.5px solid ${aktivni ? SECTION_COLOR : 'var(--border-default)'}`,
                background: aktivni ? SECTION_COLOR : 'transparent',
            }}>
            {checked ? <Check size={size - 6} strokeWidth={3} /> : indeterminate ? <Minus size={size - 6} strokeWidth={3} /> : null}
        </button>
    );
}

/**
 * Jistota AI jako tři čárky, ne přípona v desetibodovém textu. Vedle sloupce
 * názvu je to jediná věc, kterou jde přeletět očima — a přesně podle ní se
 * pozná řádek, u kterého se AI netrefila.
 */
function JistotaAI({ hodnota }) {
    if (hodnota == null) return null;
    const pct = Math.round(Number(hodnota) * 100);
    const dobra = pct >= 85;
    const plne = dobra ? 3 : pct >= 60 ? 2 : 1;
    const barva = dobra ? OK : WARN;
    return (
        <span className="inline-flex items-center gap-[3px] flex-shrink-0" title={`AI si je jistá na ${pct} %`}>
            {[0, 1, 2].map(i => (
                <span key={i} style={{
                    width: 10, height: 3, borderRadius: 2,
                    background: i < plne ? barva : 'var(--border-default)',
                }} />
            ))}
            {!dobra && <span style={{ fontSize: 9.5, fontWeight: 700, color: barva }}>AI {pct} %</span>}
        </span>
    );
}

/**
 * Jeden řádek zařazení: popisek vlevo, hodnota vpravo. Tři tyhle řádky
 * nesou to, co se u položky vybírá — P&L účet, typ a skladovou položku.
 *
 * Chybějící hodnota není pomlčka, ale tlačítko v barvě závady: červená brzí
 * schválení, oranžová je k doplnění. `prazdnyText` je pro pole, které
 * u tohohle typu nemá smysl vyplňovat — třeba katalog u služby.
 */
function ZarazeniRadek({ popisek, hodnota, chybi, chybiText, barvaChyby, prazdnyText = null, maly = false }) {
    const velikost = maly ? 11 : 11.5;
    return (
        <>
            <span className="font-bold uppercase tracking-wide"
                style={{ fontSize: maly ? 8.5 : 9.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{popisek}</span>
            {hodnota ? (
                <span className="truncate" style={{ fontSize: velikost, color: 'var(--text-primary)' }} title={hodnota}>
                    {hodnota}
                </span>
            ) : chybi ? (
                <span className="inline-flex items-center gap-1 rounded font-semibold justify-self-start"
                    style={{ padding: '1px 5px', fontSize: maly ? 10 : 10.5, background: `${barvaChyby}24`, color: barvaChyby, border: `1px solid ${barvaChyby}80` }}>
                    {chybiText} <ChevronDown size={10} />
                </span>
            ) : (
                <span className="truncate" style={{ fontSize: velikost, color: 'var(--text-tertiary)' }}>
                    {prazdnyText || '—'}
                </span>
            )}
        </>
    );
}

/** Odznak, proč je řádek vyplněný — mapování se učí, ruční zásah se nepřepisuje. */
function ZdrojOdznak({ polozka }) {
    const z = polozka.zdroj_typovani;
    if (z === 'mapping') {
        return (
            <span style={{ padding: '0 5px', borderRadius: 3, fontSize: 9.5, fontWeight: 700, background: `${OK}29`, color: OK, flexShrink: 0 }}
                title="Vyplnilo se samo — stejnou položku od tohoto dodavatele jsi už jednou zařadil">
                z mapování
            </span>
        );
    }
    if (z === 'manual') {
        return (
            <span style={{ padding: '0 5px', borderRadius: 3, fontSize: 9.5, fontWeight: 700, border: '1px solid var(--border-default)', color: 'var(--text-secondary)', flexShrink: 0 }}
                title="Zařazeno ručně">ruční</span>
        );
    }
    return null;
}

/**
 * Mřížka položek dokladu.
 *
 * Řádky jsou TEXT, ne formulář: šest trvale vykreslených dropdownů na položku
 * dělalo z šestiřádkové faktury tři obrazovky a zabíjelo možnost číst sloupec
 * svisle. Editor se otevře až pro řádek, na který se klepne (`renderEditor`) —
 * na desktopu pod řádkem, na mobilu ho volající pošle do plátna zdola.
 *
 * Chybějící buňka se nepřeškrtne, ale rovnou zve k vyplnění: řádek bez P&L
 * účtu má v jeho sloupci tlačítko, ne pomlčku.
 */
export default function PolozkyMrizka({
    polozky = [], doklad, kategorie = [], isDark = false,
    siroky = true, kompaktni = false, editovatelne = false,
    vybrane, onPrepnoutVyber, onPrepnoutVsechny, onZrusitVyber,
    otevrenaId = null, onOtevrit,
    onHromadne, hromadneBezi = false,
    renderEditor = null,
}) {
    const skupiny = useMemo(() => seskupPolozky(polozky, isDark), [polozky, isDark]);
    const ucty = useMemo(() => leafKategorie(kategorie), [kategorie]);

    // Název P&L účtu tak, jak se jmenuje — ne kód. `FC_SUROVINY` je interní
    // klíč; člověk, který doklad zařazuje, hledá „Suroviny“. Rodiče přidáváme
    // před název stejně jako `leafKategorie` v rozbalovátku, aby se řádek
    // a výběr shodovaly: pod „Ostatní náklady“ je osm podobných subúčtů.
    const rodicePodleId = useMemo(
        () => Object.fromEntries(kategorie.map(k => [k.id, k.parent_id])), [kategorie]);
    const nazvyPodleId = useMemo(
        () => Object.fromEntries(kategorie.map(k => [k.id, k.nazev])), [kategorie]);
    const uctyPodleId = useMemo(
        () => Object.fromEntries(ucty.map(u => [u.id, u.label])), [ucty]);
    const plNazev = (p) => {
        if (!p.kategorie) return null;
        if (uctyPodleId[p.kategorie_id]) return uctyPodleId[p.kategorie_id];
        const rodicId = rodicePodleId[p.kategorie_id];
        const rodic = rodicId ? nazvyPodleId[rodicId] : null;
        return rodic ? `${rodic} › ${p.kategorie.nazev}` : p.kategorie.nazev;
    };

    // Na kterou skladovou kartu je položka napojená. Relace chodí pod snake_case
    // klíči (`spotrebni_zbozi`), protože Eloquent je tak serializuje.
    const katalogNazev = (p) => p.surovina?.nazev || p.obal?.nazev || p.spotrebni_zbozi?.nazev || null;

    // Sbalené skupiny — u dokladu o dvaceti položkách se hodí zavřít to, co je hotové.
    const [sbalene, setSbalene] = useState(() => new Set());
    const prepnoutSkupinu = (klic) => setSbalene(s => {
        const n = new Set(s);
        n.has(klic) ? n.delete(klic) : n.add(klic);
        return n;
    });

    // Hromadné zařazení drží vlastní rozdělaný výběr — teprve „Použít" ho pošle.
    const [hromadnyTyp, setHromadnyTyp] = useState('');
    const [hromadnyUcet, setHromadnyUcet] = useState('');

    const sumPolozek = polozky.reduce((s, p) => s + parseFloat(p.cena_celkem_s_dph || 0), 0);
    const rozdil = parseFloat(doklad?.castka_s_dph || 0) - parseFloat(doklad?.zaokrouhleni || 0) - sumPolozek;
    const sedi = Math.abs(rozdil) <= 1;

    // Plocha „tady se pracuje" ve standardu kokpitu: neutrální závoj, ne barva.
    // Modré pozadí se v aplikaci nepoužívá — akcent nese hrana a text.
    const plochaOtevreno = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
    const plochaSkupiny = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)';

    const vsechnyVybrane = polozky.length > 0 && polozky.every(p => vybrane?.has(p.id));
    const nekterVybran = polozky.some(p => vybrane?.has(p.id));
    const hranice = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    const pouzitHromadne = () => {
        const fields = {};
        if (hromadnyTyp) fields.typ_polozky = hromadnyTyp;
        if (hromadnyUcet) fields.kategorie_id = Number(hromadnyUcet);
        if (Object.keys(fields).length === 0) return;
        onHromadne?.(fields);
        setHromadnyTyp('');
        setHromadnyUcet('');
    };

    // ── Jeden řádek ─────────────────────────────────────────────────────────
    /**
     * Zařazení položky na třech řádcích: P&L · typ · skladová položka. Týž blok
     * jede v široké mřížce i v úzké kartě — jinak by úzká varianta zase
     * neukázala, na co je položka napojená, a to je přesně ta věc, která
     * brání schválení dokladu.
     */
    const zarazeniBlok = (p, maly = false) => {
        const { chybi } = stavPolozky(p);
        const maZavadu = (kod) => chybi.some(c => c.kod === kod);
        const katalogPovinny = !!TYP_KATALOG_FK[p.typ_polozky] && p.typ_polozky !== 'phm';
        const typLabel = p.typ_polozky
            ? (TYP_VOLBY.find(t => t.value === p.typ_polozky)?.label || p.typ_polozky)
            : null;
        return (
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr',
                columnGap: 7, rowGap: maly ? 0 : 1, alignItems: 'baseline' }}>
                <ZarazeniRadek popisek="P&amp;L" hodnota={plNazev(p)} maly={maly}
                    chybi={editovatelne && maZavadu('ucet')} chybiText="Vybrat P&amp;L účet" barvaChyby={WARN} />
                <ZarazeniRadek popisek="Typ" hodnota={typLabel} maly={maly}
                    chybi={editovatelne && maZavadu('typ')} chybiText="Vybrat typ" barvaChyby={BAD} />
                <ZarazeniRadek popisek="Položka" hodnota={katalogNazev(p)} maly={maly}
                    chybi={editovatelne && maZavadu('katalog')} chybiText="Není v katalogu" barvaChyby={BAD}
                    prazdnyText={p.typ_polozky && !katalogPovinny ? '— nepotřeba —' : null} />
            </div>
        );
    };

    // ── Jeden řádek ─────────────────────────────────────────────────
    const radek = (p) => {
        const { stav, chybi } = stavPolozky(p);
        const barvaStavu = stav ? BARVA_STAVU[stav] : OK;
        const otevreno = otevrenaId === p.id;
        const zaskrtnut = !!vybrane?.has(p.id);
        const meta = `${fmt(p.mnozstvi)} ${p.jednotka?.zkratka || ''} × ${fmt(p.cena_za_jednotku)}`.replace(/\s+/g, ' ');

        // Mobil a úzký sloupec. Dřív to byla dvouřádková karta, ve které se
        // pod název vmáčkl **buď** účet, **nebo** seznam závad — na co je položka
        // napojená, se z ní nedalo přečíst nikdy. Teď nese tytéž tři řádky
        // zařazení jako široká mřížka, jen menším písmem pod sebou.
        if (!siroky) {
            return (
                <button key={p.id} type="button" onClick={() => onOtevrit?.(p.id)}
                    data-polozka={p.id}
                    className="w-full flex items-start text-left"
                    style={{
                        scrollMarginBlock: 90,
                        gap: 10, padding: kompaktni ? '9px 12px' : '8px 12px',
                        background: otevreno ? plochaOtevreno : (stav === 'red' ? `${BAD}0d` : 'transparent'),
                        border: 'none', borderBottom: `1px solid ${hranice}`, cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                    }}>
                    <span style={{ width: GRID_POL.semafor, alignSelf: 'stretch', minHeight: 34,
                        borderRadius: 2, background: barvaStavu, flexShrink: 0 }} />
                    <span className="flex-1 min-w-0">
                        <span className="flex items-baseline gap-2">
                            <span className="flex-1 min-w-0 truncate font-medium"
                                style={{ fontSize: kompaktni ? 14 : 13, color: 'var(--text-primary)' }}>
                                {p.nazev_z_dokladu}
                            </span>
                            <span className="flex-shrink-0 text-right">
                                <span className="block font-bold tabular-nums"
                                    style={{ fontSize: kompaktni ? 15 : 14, color: 'var(--text-primary)' }}>
                                    {fmt(p.cena_celkem_s_dph)}
                                </span>
                                <span className="block tabular-nums" style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                                    DPH {fmtKratce(p.dph_sazba)} %
                                </span>
                            </span>
                        </span>
                        <span className="block truncate tabular-nums" style={{ fontSize: 10.5, marginTop: 1, color: 'var(--text-tertiary)' }}>
                            {meta}
                        </span>
                        <span className="block" style={{ marginTop: 4 }}>{zarazeniBlok(p, true)}</span>
                    </span>
                    <ChevronRight size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0, marginTop: 2 }} />
                </button>
            );
        }

        // Přehled přes celou šířku — jednořádková mřížka
        return (
            <div key={p.id} data-polozka={p.id} style={{ scrollMarginBlock: 90 }}>
                <div onClick={() => onOtevrit?.(p.id)}
                    role="button" tabIndex={0} aria-expanded={otevreno}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOtevrit?.(p.id); } }}
                    className="flex items-center cursor-pointer hover:bg-black/5 dark:hover:bg-white/5"
                    style={{
                        gap: GRID_POL.mezera, padding: '7px 12px',
                        borderBottom: `1px solid ${hranice}`,
                        background: otevreno ? plochaOtevreno
                            : stav === 'red' ? `${BAD}0d`
                            : stav === 'orange' ? `${WARN}0f`
                            : undefined,
                        boxShadow: otevreno ? `inset 3px 0 0 ${SECTION_COLOR}` : undefined,
                    }}>
                    {editovatelne
                        ? <Vyberka checked={zaskrtnut} onClick={() => onPrepnoutVyber?.(p.id)} size={GRID_POL.vyberka - 8} />
                        : <span style={{ width: GRID_POL.vyberka - 8, flexShrink: 0 }} />}

                    <span style={{ width: GRID_POL.semafor, height: 28, borderRadius: 2, background: barvaStavu, flexShrink: 0 }} />

                    {/* Název má strop, aby zařazení sedělo hned vedle něj a ne až
                        u pravé hrany. Pod ním zůstává jen množství × cena za jednotku;
                        sazba DPH se přesunula k celkové částce, kam patří. */}
                    <div className="min-w-0" style={{ flex: '1 1 auto', minWidth: GRID_POL.nazevMin, maxWidth: GRID_POL.nazevMax }}>
                        <div className="truncate font-medium" style={{ fontSize: 13, color: 'var(--text-primary)' }} title={p.nazev_z_dokladu}>
                            {p.nazev_z_dokladu}
                        </div>
                        <div className="flex items-center gap-1.5 truncate" style={{ fontSize: 11, marginTop: 1, color: 'var(--text-tertiary)' }}>
                            <span className="tabular-nums flex-shrink-0">{meta}</span>
                            <ZdrojOdznak polozka={p} />
                            {p.zdroj_typovani === 'ai' && <JistotaAI hodnota={p.ai_confidence} />}
                        </div>
                    </div>

                    {/* Zařazení na třech řádcích: P&L · typ · položka z katalogu —
                        téhož tři výběry, které nabízí rozbalený editor, takže se
                        složený řádek čte stejně jako ten otevřený. Co chybí, je
                        tlačítko v barvě závady, ne pomlčka. */}
                    <div style={{ width: GRID_POL.zarazeni, flexShrink: 0 }}>
                        {zarazeniBlok(p)}
                    </div>

                    {/* Volné místo až za zařazením — částka zůstává u pravé hrany. */}
                    <span style={{ flex: '1 1 0', minWidth: 0 }} />

                    <div className="flex-shrink-0 text-right" style={{ width: GRID_POL.celkem }}>
                        <div className="tabular-nums font-semibold" style={{ fontSize: 14.5, color: 'var(--text-primary)' }}>
                            {fmt(p.cena_celkem_s_dph)}
                        </div>
                        <div className="tabular-nums" style={{ fontSize: 10.5, marginTop: 1, color: 'var(--text-tertiary)' }}>
                            DPH {fmtKratce(p.dph_sazba)} %
                        </div>
                    </div>
                </div>

                {/* Editor rozbaleného řádku — plná editace tam, kde se klepnulo */}
                {otevreno && renderEditor && (
                    <div style={{ padding: '0 10px 10px', background: plochaOtevreno, borderBottom: `1px solid ${hranice}` }}>
                        {renderEditor(p)}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-col min-h-0">
            {/* Pruh hromadného zařazení. Nejčastější práce s dokladem je „osm
                položek od téhož dodavatele, všechny surovina, všechny stejný
                účet" — po řádcích to bylo šestnáct kliknutí do dropdownů. */}
            {editovatelne && nekterVybran && (
                <div className="flex flex-wrap items-center gap-2 mb-2 p-2 rounded-xl"
                    style={{ background: plochaOtevreno, border: `1px solid ${hranice}` }}>
                    <span className="text-xs font-bold px-1" style={{ color: SECTION_COLOR }}>
                        {[...vybrane].length} vybráno
                    </span>
                    <div style={{ width: 150 }}>
                        <Dropdown value={hromadnyTyp} onChange={setHromadnyTyp}
                            options={[{ value: '', label: 'Typ…' }, ...TYP_VOLBY]}
                            placeholder="Typ…" isDark={isDark} accent={SECTION_COLOR} height={30} />
                    </div>
                    <div style={{ width: 200 }}>
                        <Dropdown value={hromadnyUcet} onChange={setHromadnyUcet}
                            options={[{ value: '', label: 'P&L účet…' }, ...ucty.map(k => ({ value: k.id, label: k.label }))]}
                            placeholder="P&L účet…" isDark={isDark} accent={SECTION_COLOR} height={30} />
                    </div>
                    <button type="button" onClick={pouzitHromadne}
                        disabled={hromadneBezi || (!hromadnyTyp && !hromadnyUcet)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold"
                        style={{
                            background: `${OK}29`, color: OK, border: `1px solid ${OK}70`,
                            opacity: hromadneBezi || (!hromadnyTyp && !hromadnyUcet) ? 0.5 : 1,
                        }}>
                        <Check size={13} /> Použít a potvrdit
                    </button>
                    <button type="button" onClick={onZrusitVyber} aria-label="Zrušit výběr"
                        className="px-2 py-1.5 rounded-lg text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        <X size={13} />
                    </button>
                </div>
            )}

            <div className="rounded-xl overflow-hidden flex flex-col min-h-0"
                style={{ border: `1px solid ${hranice}` }}>

                {/* Hlavička mřížky — jen v přehledu; v úzkém sloupci by sloupce nebyly */}
                {siroky && (
                    <div className="flex items-center flex-shrink-0"
                        style={{
                            gap: GRID_POL.mezera, padding: '7px 12px',
                            background: 'var(--surface-elevated)',
                            borderBottom: `1px solid ${hranice}`,
                        }}>
                        {editovatelne
                            ? <Vyberka checked={vsechnyVybrane} indeterminate={!vsechnyVybrane && nekterVybran}
                                onClick={onPrepnoutVsechny} size={GRID_POL.vyberka - 8} />
                            : <span style={{ width: GRID_POL.vyberka - 8, flexShrink: 0 }} />}
                        <span style={{ width: GRID_POL.semafor, flexShrink: 0 }} />
                        <span className="min-w-0 text-[10px] font-bold uppercase tracking-wide" style={{ flex: '1 1 auto', minWidth: GRID_POL.nazevMin, maxWidth: GRID_POL.nazevMax, color: 'var(--text-tertiary)' }}>Položka z dokladu</span>
                        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ width: GRID_POL.zarazeni, flexShrink: 0, color: 'var(--text-tertiary)' }}>Zařazení</span>
                        <span style={{ flex: '1 1 0', minWidth: 0 }} />
                        <span className="text-[10px] font-bold uppercase tracking-wide text-right" style={{ width: GRID_POL.celkem, flexShrink: 0, color: 'var(--text-tertiary)' }}>Celkem</span>
                    </div>
                )}

                {/* Žádný `overflow-y-auto`: tenhle blok nikdy nedostane omezenou
                    výšku (`flex-1` platí jen pod rodičem s určitou výškou a tady
                    žádný není — roluje až sloupec detailu), takže by jen rostl podle
                    obsahu. Jako scroll kontejner však pod kurzorem spolkl kolečko
                    a dál ho nepustil: nad položkami se nedalo rolovat vůbec. */}
                <div>
                    {skupiny.map(s => {
                        const zavreno = sbalene.has(s.klic);
                        return (
                            <div key={s.klic}>
                                {/* Řádek skupiny se souhrnem za typ — vzor je kategorie
                                    ve stromu surovin. „Bez typu" je červená a jde první,
                                    protože je to jediné, co tu brání schválení. */}
                                <div onClick={() => prepnoutSkupinu(s.klic)}
                                    className="flex items-center cursor-pointer"
                                    style={{
                                        gap: GRID_POL.mezera, padding: '7px 12px', minHeight: kompaktni ? 40 : undefined,
                                        background: s.blokuje ? `${BAD}1a` : plochaSkupiny,
                                        borderBottom: `1px solid ${s.blokuje ? `${BAD}47` : hranice}`,
                                    }}>
                                    {zavreno
                                        ? <ChevronRight size={14} style={{ color: s.blokuje ? BAD : 'var(--text-secondary)', flexShrink: 0 }} />
                                        : <ChevronDown size={14} style={{ color: s.blokuje ? BAD : 'var(--text-secondary)', flexShrink: 0 }} />}
                                    <span style={{ width: 9, height: 9, borderRadius: 2, background: s.barva, flexShrink: 0 }} />
                                    <span className="min-w-0 truncate font-bold"
                                        style={{ flex: '1 1 auto', minWidth: GRID_POL.nazevMin, maxWidth: GRID_POL.nazevMax,
                                            fontSize: 12.5, color: s.blokuje ? BAD : 'var(--text-primary)' }}>
                                        {s.label} <span style={{ fontWeight: 500, color: s.blokuje ? BAD : 'var(--text-tertiary)' }}>{s.polozky.length}</span>
                                        {s.blokuje && <span style={{ fontWeight: 500 }}> — brání schválení</span>}
                                    </span>
                                    {siroky && (
                                        <span className="truncate" style={{ width: GRID_POL.zarazeni, flexShrink: 0, fontSize: 11, color: 'var(--text-tertiary)' }}
                                            title={s.ucty.join(' · ')}>
                                            {s.ucty.join(' · ')}
                                        </span>
                                    )}
                                    {siroky && <span style={{ flex: '1 1 0', minWidth: 0 }} />}
                                    <span className="flex-shrink-0 text-right tabular-nums font-bold"
                                        style={{ width: GRID_POL.celkem, fontSize: 14.5, color: s.blokuje ? BAD : 'var(--text-primary)' }}>
                                        {fmt(s.soucet)}
                                    </span>
                                </div>
                                {!zavreno && s.polozky.map(radek)}
                            </div>
                        );
                    })}
                </div>

                {/* Patička: součet položek proti celkové částce dokladu. Tuhle
                    kontrolu dělal jen server ve validaci po pokusu o schválení —
                    přitom se na ni člověk ptá u každého zapsaného řádku. */}
                <div className="flex items-center flex-shrink-0 tabular-nums"
                    style={{
                        gap: 9, padding: '9px 12px', minHeight: kompaktni ? 44 : undefined,
                        background: 'var(--surface-elevated)',
                        borderTop: `1px solid ${hranice}`,
                    }}>
                    {sedi
                        ? <Check size={15} strokeWidth={2.6} style={{ color: OK, flexShrink: 0 }} />
                        : <X size={15} strokeWidth={2.6} style={{ color: BAD, flexShrink: 0 }} />}
                    <span className="flex-1 min-w-0 truncate" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {sedi ? 'Souhlasí s celkovou částkou dokladu' : `Rozdíl ${fmtKratce(Math.abs(rozdil))} Kč proti dokladu`}
                    </span>
                    <span className="flex-shrink-0 font-bold" style={{ fontSize: 13, color: sedi ? 'var(--text-primary)' : BAD }}>
                        {siroky
                            ? `${fmt(sumPolozek)} / ${fmt(doklad?.castka_s_dph)} Kč`
                            : `${fmt(sumPolozek)} Kč`}
                    </span>
                </div>
            </div>
        </div>
    );
}
